import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@shared/store/dialog.duck';
import DialogHeader from '@shared/dialogs/DialogHeader';
import Dialog from "@shared/ui/molecules/Dialog";
import { Column } from "@shared/ui/atoms/StructuralLayout";
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import { selectAllTeams } from '@shared/store/config.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import styled from "styled-components";
import { BodyRegular, BodySmall } from "@shared/ui/atoms/fonts/Body";
import { useTranslation } from "react-i18next";
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import Colours from '@shared/ui/atoms/Colours';
import {
  loadFollowingGroups,
  selectFollowingTeams,
  selectFollowingWorkGroups,
  selectIsLoading,
  updateFollowingTeamsAndWorkgroups
} from '@features/whereabouts/store/followingTeamsAndTags.duck';
import { TeamWithCount } from '@shared/models/team.models';
import { TagWithCount } from '@shared/models/tags.models';

export function FollowTeamsAndWorkgroupsDialog() {
  const dispatch = useDispatch();
  const allTeams = useSelector(selectAllTeams);
  const allWorkGroups = useSelector(selectAllTags);
  const followedTeams = useSelector(selectFollowingTeams);
  const followedWorkGroups = useSelector(selectFollowingWorkGroups);
  const isLoading = useSelector(selectIsLoading);
  const {t} = useTranslation();

  useEffect(() => {
    dispatch(loadFollowingGroups(true));
  }, [dispatch]);

  const isTeamFollowed = (teamId: string) => {
    return followedTeams.some(ft => ft.id === teamId);
  }

  const isWorkGroupFollowed = (workGroupId: number) => {
    return followedWorkGroups.some(fwg => fwg.id === workGroupId);
  }

  const onTeamToggled = (team: TeamWithCount, checked: boolean) => {
    const currentTeams = [...followedTeams];
    const currentWorkGroups = [...followedWorkGroups];

    if (checked) {
      const teamEntity = { id: team.id, name: team.name, companyId: '', value: {} as any };
      dispatch(updateFollowingTeamsAndWorkgroups({
        teams: [...currentTeams, teamEntity],
        workGroups: currentWorkGroups,
        reloadTeamWhereabouts: true,
      }));
    } else {
      dispatch(updateFollowingTeamsAndWorkgroups({
        teams: currentTeams.filter(ft => ft.id !== team.id),
        workGroups: currentWorkGroups,
        reloadTeamWhereabouts: true,
      }));
    }
  }

  const onWorkGroupToggled = (workGroup: TagWithCount, checked: boolean) => {
    const currentTeams = [...followedTeams];
    const currentWorkGroups = [...followedWorkGroups];

    if (checked) {
      const tag = { id: workGroup.id, companyId: workGroup.companyId, label: workGroup.label };
      dispatch(updateFollowingTeamsAndWorkgroups({
        teams: currentTeams,
        workGroups: [...currentWorkGroups, tag],
        reloadTeamWhereabouts: true,
      }));
    } else {
      dispatch(updateFollowingTeamsAndWorkgroups({
        teams: currentTeams,
        workGroups: currentWorkGroups.filter(fwg => fwg.id !== workGroup.id),
        reloadTeamWhereabouts: true,
      }));
    }
  }

  const close = () => {
    dispatch(closeDialog());
  }

  return (
    <DialogWrapper isOpen={true} onClose={close} showLogo={true}>
      <DialogHeader title="dialog-headers.follow-teams-and-workgroups" />

      <BodyRegular style={{marginBottom: 16}}>
        {t('follow-dialog.description')}
      </BodyRegular>

      <ScrollableList>
        {allTeams.length > 0 && <>
          <SectionHeader>{t('team-view.teams')}</SectionHeader>
          {allTeams.map((team: TeamWithCount) => (
            <ListItem key={`team-${team.id}`}>
              <Checkbox value={isTeamFollowed(team.id)}
                        onChange={(checked: boolean) => onTeamToggled(team, checked)}
                        disabled={isLoading}>
                <ItemContent>
                  <BodyRegular>{team.name}</BodyRegular>
                  <BodySmall colour={Colours.darkGrey}>{team.memberCount} {t('follow-dialog.members')}</BodySmall>
                </ItemContent>
              </Checkbox>
            </ListItem>
          ))}
        </>}

        {allWorkGroups.length > 0 && <>
          <SectionHeader>{t('team-view.workgroup')}</SectionHeader>
          {allWorkGroups.map((workGroup: TagWithCount) => (
            <ListItem key={`wg-${workGroup.id}`}>
              <Checkbox value={isWorkGroupFollowed(workGroup.id)}
                        onChange={(checked: boolean) => onWorkGroupToggled(workGroup, checked)}
                        disabled={isLoading}>
                <ItemContent>
                  <BodyRegular>{workGroup.label}</BodyRegular>
                  <BodySmall colour={Colours.darkGrey}>{workGroup.memberCount} {t('follow-dialog.members')}</BodySmall>
                </ItemContent>
              </Checkbox>
            </ListItem>
          ))}
        </>}
      </ScrollableList>

      <Column style={{marginTop: 'auto', paddingTop: 16}}>
        <PrimaryButton fullWidth={true}
                       click={close}
                       text="button.done" />
      </Column>
    </DialogWrapper>
  )
}

const DialogWrapper = styled(Dialog)<any>`
  &.dialog {
    max-width: 100%;
  }
  .dialog__contentContainer, .dialog__content {
    width: 500px;
    min-height: 400px;
    max-height: 80vh;
  }
  .dialog__content {
    display: flex;
    flex-direction: column;
  }
`

const ScrollableList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
`

const SectionHeader = styled(BodyRegular)`
  font-weight: 600;
  color: ${Colours.blue};
  padding: 12px 16px 4px;
`

const ListItem = styled.div`
  padding: 8px 16px;
  &:hover {
    background: ${Colours.veryLightGrey};
  }
  .checkbox__container {
    width: 100%;
  }
`

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
`
